/**
 * Created by Vinicius
 */

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  takeUntil,
} from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { fromEvent, Observable } from 'rxjs';
import { NbDialogService, NbToastrService } from '@nebular/theme';

import {
  BasePlanFilter,
  FitnessPlanData,
  MealPlanData,
  ProgramTemplateData,
} from 'app/@core/interfaces/common/CalorieFriend/plan';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { MealPlanTemplateCreateComponent } from 'app/admin/public-templates/template-create/template-create.component';

import { FitnessPlanTemplateCreateComponent } from '../../FitnessPlans/plan-template-create/plan-template-create.component';
import { ProgramTemplateCreateComponent } from '../../program-templates/program-template-create/program-template-create.component';

@Component({
  selector: 'app-plan-template-list',
  templateUrl: './template-list.component.html',
  styleUrls: ['./template-list.component.scss'],
})
export class PlanTemplateListComponent implements OnInit, OnDestroy {
  @Input() title: string = '';
  @Input() readonly: boolean = true;
  @Input() set publicOnly(value: boolean) {
    if (value === undefined) delete this.paging.publicOnly;
    else this.paging.publicOnly = value;
    this.loadDataList();
  }
  @Input() set planType(value: string) {
    if (!value?.length) return;
    this.isMealPlan = value.toUpperCase() === 'MEAL'; // MEAL or WORKOUT
    this.isProgramTemplate = value.toUpperCase() === 'PROGRAM'; // PROGRAM or TEMPLATE
    this.editUrl =
      '/pages/CalorieFriend/' +
      (this.isMealPlan ? 'Template/' : this.isProgramTemplate ? 'program-template/' :'FitnessTemplate/');
  }

  @ViewChild(MatPaginator, { static: true }) paginator?: MatPaginator;
  @ViewChild('search', { static: true }) searchInput?: ElementRef;

  readonly displayedColumns = ['name', 'description', 'actions','assing'];
  totalCount: number = 0;
  dataList: any[] = [];
  editUrl: string;
  contentLoaded:boolean = false;

  private unsubscribe$ = new Subject<void>();
  private paging: BasePlanFilter = {};
  public isMealPlan: boolean;
  public isProgramTemplate: boolean;

  constructor(
    private mealPlanService: MealPlanData,
    private fitnessPlanService: FitnessPlanData,
    private programTemplateService: ProgramTemplateData,
    private toasterService: NbToastrService,
    private dialogService: NbDialogService,
  ) {}

  ngOnInit(): void {
    fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((x) => x !== this.paging.name),
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe(() => {
        this.paging.name = this.searchInput.nativeElement.value;
        this.loadDataList();
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSortChanged(sort: Sort): void {
    this.paging.sortBy = sort.active;
    this.paging.orderBy = sort.direction;
    this.loadDataList(true);
  }

  private dataService(): MealPlanData | FitnessPlanData | ProgramTemplateData {
    if(this.isProgramTemplate) {
      return this.programTemplateService;
    }
    return this.isMealPlan ? this.mealPlanService : this.fitnessPlanService;
  }

  loadDataList(pagination: boolean = false): void {
    if (!pagination && this.paginator) this.paginator.pageIndex = 0;
    this.paging.pageNumber = pagination
      ? this.paginator?.pageIndex + 1 ?? 1
      : 1;
    this.paging.pageSize = this.paginator?.pageSize ?? 10;

    (this.dataService().templateList(this.paging) as Observable<any>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((res) => {
        this.totalCount = res.totalCount;
        this.dataList = res.items;
        this.contentLoaded = true;
      });
  }

  onAddTemplate(): void {
    if (this.readonly) return;
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Do you want to create a template?' },
      })
      .onClose.subscribe((result) => {
        if (!result) return;
        const option = {
          context: { isPublic: false },
          closeOnEsc: false,
          closeOnBackdropClick: false,
        };
        (this.isMealPlan
          ? this.dialogService.open(MealPlanTemplateCreateComponent, option)
          : this.isProgramTemplate
          ? this.dialogService.open(ProgramTemplateCreateComponent, option)
          : this.dialogService.open(FitnessPlanTemplateCreateComponent, option)
        ).onClose.subscribe(() => this.loadDataList(true));
      });
  }

  onDuplicate(id: number): void {
    if (this.readonly) return;
    (this.dataService().duplicateTemplate(id) as Observable<any>)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        () => {
          this.toasterService.success('', 'Template has been duplicated!');
          this.loadDataList(true);
        },
        () =>
          this.toasterService.danger(
            '',
            'There was a problem duplicating the template!',
          ),
      );
  }

  onDelete(id: number): void {
    if (this.readonly) return;
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure you want to delete the template?' },
      })
      .onClose.subscribe((result) => {
        if (!result) return;
        (this.dataService().deleteTemplate(id) as Observable<any>)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this.toasterService.success('', 'Template has been deleted');
              this.loadDataList(true);
            },
            () =>
              this.toasterService.danger(
                '',
                'There was a problem deleting the template!',
              ),
          );
      });
  }
}
